import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  Badge,
  Button,
  Field,
  Input,
  LoadingState,
  ErrorState,
  EmptyState,
} from "@/components/design-system";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import {
  adminListUsers,
  adminUserOptions,
  adminUpdateUser,
  adminDeleteUser,
} from "@/lib/admin";

const cn = (...xs) => xs.filter(Boolean).join(" ");

const ROLE_TINT = {
  admin: "admin",
  founder: "founder",
  community: "community",
  ambassador: "ambassador",
};

const TIER_LABEL = {
  explorer: "Explorer",
  validator: "Validator",
  ambassador_eligible: "Ambassador eligible",
};

function RolesCell({ roles }) {
  return (
    <div className="flex flex-wrap gap-1">
      {(roles || []).map((r) => (
        <Badge key={r} variant={ROLE_TINT[r] || "neutral"}>{r}</Badge>
      ))}
    </div>
  );
}

function planLabel(planKey, options) {
  const p = options?.plans?.find((x) => x.key === planKey);
  return p ? `${p.label}${p.display_label && p.display_label !== "—" ? ` · ${p.display_label}` : ""}` : (planKey || "free");
}

// ---------- Editor modal ---------------------------------------------------
function EditUserModal({ user, options, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    roles: user.roles || [],
    active_role: user.active_role || (user.roles || [])[0] || "community",
    plan: user.plan || "free",
    tier: user.tier || "explorer",
    reputation_points: user.reputation_points ?? 0,
    ambassador_status: user.ambassador_status || "none",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const toggleRole = (r) => {
    setForm((f) => {
      const has = f.roles.includes(r);
      const next = has ? f.roles.filter((x) => x !== r) : [...f.roles, r];
      const active = next.includes(f.active_role) ? f.active_role : next[0];
      return { ...f, roles: next, active_role: active };
    });
  };

  const save = async () => {
    setBusy(true); setError("");
    try {
      // Only send changed fields (backend accepts partial patches)
      const patch = {};
      if (form.name !== user.name) patch.name = form.name;
      if (JSON.stringify(form.roles.sort()) !== JSON.stringify((user.roles || []).slice().sort())) patch.roles = form.roles;
      if (form.active_role !== user.active_role) patch.active_role = form.active_role;
      if (form.plan !== (user.plan || "free")) patch.plan = form.plan;
      if (form.tier !== user.tier) patch.tier = form.tier;
      if (Number(form.reputation_points) !== Number(user.reputation_points)) patch.reputation_points = Number(form.reputation_points);
      if (form.ambassador_status !== user.ambassador_status) patch.ambassador_status = form.ambassador_status;
      const updated = await adminUpdateUser(user.id, patch);
      onSaved(updated);
      toast.success("User updated.");
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="admin-user-edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-6 border-b border-[#0B1F3A]/10">
          <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/60 font-bold">Editing user</div>
          <h2 className="font-[Montserrat] font-bold text-2xl text-[#0B1F3A] mt-1">{user.name || "(no name)"}</h2>
          <div className="text-sm text-[#0B1F3A]/65">{user.email}</div>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-user-name" />
          </Field>
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-[#0B1F3A]/60 mb-2">Roles</div>
            <div className="flex flex-wrap gap-2">
              {options.roles.map((r) => {
                const on = form.roles.includes(r);
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => toggleRole(r)}
                    data-testid={`edit-user-role-${r}`}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border",
                      on ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "bg-white text-[#0B1F3A] border-[#0B1F3A]/20 hover:border-[#0B1F3A]/40"
                    )}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
          {form.roles.length > 1 && (
            <Field label="Active role">
              <select
                value={form.active_role}
                onChange={(e) => setForm({ ...form, active_role: e.target.value })}
                data-testid="edit-user-active-role"
                className="w-full rounded-lg border border-[#0B1F3A]/15 bg-white px-3 py-2 text-sm text-[#0B1F3A]"
              >
                {form.roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          )}
          <Field label="Subscription plan">
            <select
              value={form.plan}
              onChange={(e) => setForm({ ...form, plan: e.target.value })}
              data-testid="edit-user-plan"
              className="w-full rounded-lg border border-[#0B1F3A]/15 bg-white px-3 py-2 text-sm text-[#0B1F3A]"
            >
              {options.plans.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label} {p.display_label && p.display_label !== "—" ? `· ${p.display_label}` : ""}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Tier">
              <select
                value={form.tier}
                onChange={(e) => setForm({ ...form, tier: e.target.value })}
                data-testid="edit-user-tier"
                className="w-full rounded-lg border border-[#0B1F3A]/15 bg-white px-3 py-2 text-sm text-[#0B1F3A]"
              >
                {options.tiers.map((t) => (
                  <option key={t.key} value={t.key}>{TIER_LABEL[t.key] || t.key} · ≥ {t.min_points} pts</option>
                ))}
              </select>
            </Field>
            <Field label="Reputation points">
              <Input
                type="number"
                min={0}
                max={100000}
                value={form.reputation_points}
                onChange={(e) => setForm({ ...form, reputation_points: e.target.value })}
                data-testid="edit-user-reputation"
              />
            </Field>
          </div>
          <Field label="Ambassador status">
            <select
              value={form.ambassador_status}
              onChange={(e) => setForm({ ...form, ambassador_status: e.target.value })}
              data-testid="edit-user-ambassador-status"
              className="w-full rounded-lg border border-[#0B1F3A]/15 bg-white px-3 py-2 text-sm text-[#0B1F3A]"
            >
              {options.ambassador_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="p-6 border-t border-[#0B1F3A]/10 flex justify-between gap-3">
          <Button variant="ghost" onClick={onClose} data-testid="edit-user-cancel">Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy} data-testid="edit-user-save">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Delete confirm -------------------------------------------------
function DeleteConfirm({ user, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const canDelete = confirmText.trim().toLowerCase() === "delete";

  const del = async () => {
    setBusy(true);
    try {
      await adminDeleteUser(user.id);
      onDeleted();
      toast.success(`User ${user.email} deleted.`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="admin-user-delete-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-[#0B1F3A]/10">
          <Badge variant="admin">Danger zone</Badge>
          <h2 className="font-[Montserrat] font-bold text-2xl text-[#0B1F3A] mt-3">Delete this user?</h2>
          <p className="text-sm text-[#0B1F3A]/70 mt-2">
            This permanently deletes <b>{user.email}</b> and cascade-removes their products, campaigns,
            feedback, reputation events, notifications, referral codes and subscriptions. This cannot be undone.
          </p>
        </div>
        <div className="p-6 space-y-3">
          <Field label={<>Type <b>delete</b> to confirm</>}>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="delete-user-confirm-input" />
          </Field>
        </div>
        <div className="p-6 border-t border-[#0B1F3A]/10 flex justify-between gap-3">
          <Button variant="ghost" onClick={onClose} data-testid="delete-user-cancel">Cancel</Button>
          <Button variant="danger" onClick={del} disabled={!canDelete || busy} data-testid="delete-user-confirm">
            {busy ? "Deleting…" : "Delete user"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Page -----------------------------------------------------------
export default function AdminUsers() {
  const { user: me } = useAuth();
  const [state, setState] = useState({ status: "loading", items: [], total: 0, err: "" });
  const [options, setOptions] = useState(null);
  const [filters, setFilters] = useState({ q: "", role: "", tier: "", plan: "", ambassador_status: "" });
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const params = { page, page_size: 25 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [data, opts] = await Promise.all([
        adminListUsers(params),
        options ? Promise.resolve(options) : adminUserOptions(),
      ]);
      setOptions(opts);
      setState({ status: "ready", items: data.items, total: data.total, err: "" });
    } catch (err) {
      setState({ status: "error", items: [], total: 0, err: formatApiError(err) });
    }
  };
  useEffect(() => { load(); }, [filters, page]);

  const totalPages = Math.max(1, Math.ceil(state.total / 25));

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const activeFilters = useMemo(() =>
    Object.entries(filters).filter(([, v]) => !!v).map(([k, v]) => `${k}:${v}`),
    [filters]);

  return (
    <div data-testid="admin-users-page">
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Administrator</p>
      <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">Users</h1>
      <p className="text-[#0B1F3A]/70 mt-2 max-w-3xl">
        Every registered account. Search, filter, change roles / plans / tiers on the fly, or delete accounts (with a full cascade of their data).
      </p>

      <Card className="mt-8">
        <form onSubmit={applySearch} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end" data-testid="admin-users-filters">
          <div className="md:col-span-2">
            <Field label="Search">
              <Input
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="email or name"
                data-testid="admin-users-search"
              />
            </Field>
          </div>
          {[
            { k: "role", label: "Role", opts: options?.roles || [] },
            { k: "tier", label: "Tier", opts: (options?.tiers || []).map((t) => t.key) },
            { k: "plan", label: "Plan", opts: (options?.plans || []).map((p) => p.key) },
            { k: "ambassador_status", label: "Ambassador", opts: options?.ambassador_statuses || [] },
          ].map(({ k, label, opts }) => (
            <Field key={k} label={label}>
              <select
                value={filters[k]}
                onChange={(e) => { setFilters({ ...filters, [k]: e.target.value }); setPage(1); }}
                data-testid={`admin-users-filter-${k}`}
                className="w-full rounded-lg border border-[#0B1F3A]/15 bg-white px-3 py-2 text-sm text-[#0B1F3A]"
              >
                <option value="">All</option>
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          ))}
        </form>
        {activeFilters.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {activeFilters.map((f) => <span key={f} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#E8F1FF] text-[#0B1F3A]/70 font-bold">{f}</span>)}
            <button
              type="button"
              onClick={() => { setFilters({ q: "", role: "", tier: "", plan: "", ambassador_status: "" }); setPage(1); }}
              className="text-[10px] uppercase tracking-widest text-[#2D7FF9] font-bold hover:text-[#0B1F3A]"
              data-testid="admin-users-clear-filters"
            >
              Clear filters
            </button>
          </div>
        )}
      </Card>

      <div className="mt-6">
        {state.status === "loading" && <LoadingState label="Loading users…" />}
        {state.status === "error" && <ErrorState detail={state.err} action={<Button variant="secondary" onClick={load}>Retry</Button>} />}
        {state.status === "ready" && state.items.length === 0 && (
          <EmptyState title="No users match" description="Try clearing the search or a filter." />
        )}
        {state.status === "ready" && state.items.length > 0 && (
          <Card className="!p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#E8F1FF] text-[10px] uppercase tracking-widest text-[#0B1F3A]/70">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold">User</th>
                    <th className="text-left px-4 py-3 font-bold">Roles</th>
                    <th className="text-left px-4 py-3 font-bold">Plan</th>
                    <th className="text-left px-4 py-3 font-bold">Tier</th>
                    <th className="text-right px-4 py-3 font-bold">Points</th>
                    <th className="text-left px-4 py-3 font-bold">Ambassador</th>
                    <th className="text-right px-4 py-3 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#0B1F3A]/5">
                  {state.items.map((u) => {
                    const isMe = u.id === me?.id;
                    return (
                      <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="hover:bg-[#E8F1FF]/30">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-[#0B1F3A]">{u.name || "—"} {isMe && <span className="text-[9px] font-black uppercase tracking-widest text-[#2D7FF9] ml-1">You</span>}</div>
                          <div className="text-xs text-[#0B1F3A]/60">{u.email}</div>
                        </td>
                        <td className="px-4 py-3"><RolesCell roles={u.roles} /></td>
                        <td className="px-4 py-3 text-[#0B1F3A]/80 whitespace-nowrap">{planLabel(u.plan, options)}</td>
                        <td className="px-4 py-3 text-[#0B1F3A]/80">{TIER_LABEL[u.tier] || u.tier}</td>
                        <td className="px-4 py-3 text-right font-[Montserrat] font-extrabold text-[#0B1F3A] tabular-nums">{u.reputation_points}</td>
                        <td className="px-4 py-3">
                          {u.ambassador_status === "verified" ? (
                            <Badge variant="ambassador">Verified</Badge>
                          ) : u.ambassador_status && u.ambassador_status !== "none" ? (
                            <Badge variant="neutral">{u.ambassador_status}</Badge>
                          ) : (
                            <span className="text-[#0B1F3A]/40">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <Button variant="secondary" onClick={() => setEditing(u)} data-testid={`admin-user-edit-${u.id}`}>Edit</Button>{" "}
                          <Button
                            variant="danger"
                            onClick={() => setDeleting(u)}
                            disabled={isMe}
                            title={isMe ? "You can't delete your own account" : ""}
                            data-testid={`admin-user-delete-${u.id}`}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="p-4 flex items-center justify-between text-xs text-[#0B1F3A]/65 border-t border-[#0B1F3A]/10">
              <span data-testid="admin-users-total"><b className="text-[#0B1F3A]">{state.total}</b> user{state.total === 1 ? "" : "s"}</span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="admin-users-prev">← Prev</Button>
                <span>Page <b className="text-[#0B1F3A]">{page}</b> / {totalPages}</span>
                <Button variant="ghost" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="admin-users-next">Next →</Button>
              </div>
            </div>
          </Card>
        )}
      </div>

      {editing && options && (
        <EditUserModal
          user={editing}
          options={options}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setEditing(null);
            setState((s) => ({ ...s, items: s.items.map((x) => (x.id === updated.id ? updated : x)) }));
          }}
        />
      )}
      {deleting && (
        <DeleteConfirm
          user={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />
      )}
    </div>
  );
}
